import React, { Key, ReactElement } from 'react'
import {
  Control,
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  PathValue,
  Validate,
  ValidationRule,
} from 'react-hook-form'
import { Autocomplete, Box, Chip, FilterOptionsState, createFilterOptions } from '@mui/material'
import { useTranslate } from 'src/i18n/useMessageSource'
import { findReferenceObject } from 'src/service/view-model/base/Reference.utils'
import { getActiveValueLabel, renderHelperText } from 'src/ui-shared/base/form/control-hook-form/HookForm'
import { TextFieldDefault } from 'src/ui-shared/base/form/control/TextFieldDefault'

type TFreeSoloOption<TOption> = TOption | string

interface TransformObject<T> {
  labelFieldName: keyof T
  valueFieldName: keyof T
  translateLabel?: boolean
}

interface MultiSelectWrapperProps<T extends FieldValues, TOption, FieldPropertyName extends Path<T>> {
  name: FieldPropertyName
  options: TOption[]
  control: Control<T, any>
  label: string
  activeValue?: string[]
  validate?:
    | Validate<PathValue<T, FieldPropertyName>, T>
    | Record<string, Validate<PathValue<T, FieldPropertyName>, T>>
    | undefined
  required?: ValidationRule<boolean>
  disabled?: boolean
  disableClearable?: boolean
  transform: TransformObject<TOption>
  hintText?: string
  onChangeEventListener?: (field: ControllerRenderProps<T, FieldPropertyName>, fieldState: ControllerFieldState) => void
}

export const MultipleChoiceFreeSoloAutocompleteHookForm = <
  T extends FieldValues,
  TOption,
  FieldPropertyName extends Path<T>,
>({
  control,
  name,
  label,
  activeValue,
  validate,
  required,
  options,
  disabled,
  disableClearable,
  transform,
  hintText,
  onChangeEventListener,
}: MultiSelectWrapperProps<T, TOption, FieldPropertyName>): ReactElement => {
  const translate = useTranslate()

  const handleOnChange = (
    _event: unknown,
    value: TFreeSoloOption<TOption>[] | null,
    field: ControllerRenderProps<T, FieldPropertyName>,
    fieldState: ControllerFieldState,
  ) => {
    if (!value || value.length === 0) {
      field.onChange([])
      return
    }

    const uniqueValues = new Set<string>()

    value.forEach((item) => {
      if (typeof item === 'string') {
        const currentValue = item.trim()

        if (currentValue) {
          uniqueValues.add(currentValue)
        }
      } else if (item) {
        const currentValue = item[transform.labelFieldName]
        if (currentValue) {
          uniqueValues.add(String(currentValue))
        }
      }
    })

    const resultOptions: TOption[] = Array.from(uniqueValues).map((strValue) => {
      const matchedOption = options.find((opt) => String(opt[transform.labelFieldName]) === strValue)

      return matchedOption !== undefined ? matchedOption : newFreeSoloSelection(strValue)
    })

    field.onChange(resultOptions)

    if (onChangeEventListener) {
      onChangeEventListener(field, fieldState)
    }
  }

  const newFreeSoloSelection = (value: string): TOption => {
    const newOption: TOption = {
      [transform.valueFieldName]: null,
      [transform.labelFieldName]: value,
    } as TOption

    return newOption
  }

  const isNewFreeSoloSelection = (option: TFreeSoloOption<TOption>): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (typeof option !== 'string' && option[transform.valueFieldName] === null) {
      return true
    }
    return false
  }

  const renderOptionLabel = (): ((option: TFreeSoloOption<TOption>) => string) => {
    return (option) => {
      if (typeof option === 'string') {
        return `${option} (${translate('newLabel')})`
      } else if (isNewFreeSoloSelection(option)) {
        return `${option[transform.labelFieldName]} (${translate('newLabel')})`
      } else {
        let label = option[transform.labelFieldName] as unknown as string

        if (transform.translateLabel) {
          label = translate(label)
        }

        return label
      }
    }
  }

  const getOptionSelected = (option: TFreeSoloOption<TOption>, value: TFreeSoloOption<TOption>) => {
    if (!option || !value) return option === value

    if (typeof option === 'string' || typeof value === 'string') {
      return option === value
    }

    return option[transform.labelFieldName] === value[transform.labelFieldName]
  }

  const getSelectedOptions = (values: any[] = []): TFreeSoloOption<TOption>[] => {
    return values.map((value) => {
      const foundOption = options.find((item) => item[transform.labelFieldName] === value)
      return foundOption || value
    })
  }

  const renderOptions = ():
    | ((props: React.HTMLAttributes<HTMLLIElement>, option: TFreeSoloOption<TOption>) => React.ReactNode)
    | undefined => {
    const renderLabel = renderOptionLabel()!

    return function AutocompleteOptionComponent(props, option) {
      const key = typeof option === 'string' ? option : (option[transform.valueFieldName] as unknown as Key)
      return (
        <li {...props} key={key}>
          {renderLabel(option)}
        </li>
      )
    }
  }

  const filterOptionsWithAddNew = (
    options: TFreeSoloOption<TOption>[],
    params: FilterOptionsState<TFreeSoloOption<TOption>>,
  ): TFreeSoloOption<TOption>[] => {
    const { inputValue } = params
    const filtered = createFilterOptions<TFreeSoloOption<TOption>>({
      stringify: renderOptionLabel(),
      trim: true,
      ignoreCase: true,
      matchFrom: 'any',
    })(options, params)

    const trimmedInput = inputValue.trim()
    const inputExists = trimmedInput.length > 0

    const newAddOption = newFreeSoloSelection(trimmedInput)

    const isExistingOption = !filtered.some((option) => getOptionSelected(option, newAddOption))

    if (inputExists && isExistingOption) {
      return [...filtered, newAddOption]
    }

    return filtered
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate: validate, required: required }}
      render={({ field, fieldState }) => {
        let labelToUse = label
        if (activeValue !== undefined) {
          const activeValueLabels = activeValue
            .map((val) => {
              // @ts-ignore
              const activeValueLabel = findReferenceObject(options, val, 'data') as SettingSelectFieldOption
              return activeValueLabel ? activeValueLabel.label : val
            })
            .join(', ')

          labelToUse = getActiveValueLabel(translate, label, activeValueLabels)
        }

        return (
          <Box>
            <Autocomplete
              multiple
              freeSolo={true}
              disabled={disabled}
              options={options}
              filterOptions={filterOptionsWithAddNew}
              clearOnBlur={true}
              clearOnEscape={true}
              {...field}
              onChange={(_event, value) => handleOnChange(_event, value, field, fieldState)}
              renderInput={(params) => (
                <TextFieldDefault variant="filled" {...params} label={labelToUse} error={!!fieldState.error} />
              )}
              disableClearable={disableClearable}
              isOptionEqualToValue={getOptionSelected}
              getOptionLabel={renderOptionLabel()}
              value={getSelectedOptions(field.value)}
              renderOption={renderOptions()}
              renderValue={(value, getTagProps) => {
                return value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={renderOptionLabel()(option)}
                    {...getTagProps({ index })}
                    key={index}
                  />
                ))
              }}
            />
            {renderHelperText(fieldState, translate, hintText)}
          </Box>
        )
      }}
    />
  )
}
